// Calls onDenied({ operation, paths, collections, error }) whenever Firestore refuses a request made through the
// compat SDK, whichever code made it. The original error still reaches the caller.

const observerMethods = ["next", "error", "complete"];

function isObserver(arg) {
	return (
		typeof arg === "object" && arg !== null && observerMethods.some((method) => typeof arg[method] === "function")
	);
}

function queryPath(query) {
	try {
		const { path, collectionGroup } = query._delegate._query;

		return collectionGroup ? `**/${collectionGroup}` : path.canonicalString();
	} catch {
		return "unknown";
	}
}

// "users/abc/settings/def" -> "users/{id}/settings/{id}"
export function collectionOf(path) {
	if (path.startsWith("**/")) {
		return path;
	}

	return path
		.split("/")
		.map((segment, index) => (index % 2 === 1 ? "{id}" : segment))
		.join("/");
}

// One Sentry event per operation and paths per page load, grouped by collection. The event id goes on the
// error (sentryEventId), so whoever shows or reports the error later can reuse the event instead of sending another.
export function reportToSentry(Sentry) {
	const eventIds = new Map();

	return ({ operation, paths, collections, error }) => {
		const key = `${operation} ${paths.join(" ")}`;

		if (!eventIds.has(key)) {
			const exception =
				error ||
				Object.assign(new Error("Missing or insufficient permissions."), { code: "permission-denied" });

			eventIds.set(
				key,
				Sentry.captureException(exception, {
					level: "warning",
					tags: { firestoreOperation: operation, firestoreCollection: collections[0] },
					extra: { paths: paths.slice(0, 20), collections },
					fingerprint: ["firestore-refused", operation, ...collections],
				}),
			);
		}

		if (error && typeof error === "object") {
			error.sentryEventId = eventIds.get(key);
		}
	};
}

export default function watchPermissionDenied(firestore, onDenied) {
	const { DocumentReference, Query, WriteBatch, Transaction, Firestore } = firestore;

	if (DocumentReference.prototype.__watchPermissionDenied) {
		return;
	}

	DocumentReference.prototype.__watchPermissionDenied = true;

	const denied = (operation, paths, error) => {
		onDenied({ operation, paths, collections: [...new Set(paths.map(collectionOf))], error });
	};

	const rethrow = (operation, paths) => (error) => {
		if (error?.code === "permission-denied") {
			denied(operation, paths, error);
		}

		throw error;
	};

	const wrapPromise = (prototype, method, operation, getPaths) => {
		const original = prototype[method];

		prototype[method] = function (...args) {
			return original.apply(this, args).catch(rethrow(operation, getPaths(this)));
		};
	};

	const wrapSnapshot = (prototype, getPaths) => {
		const original = prototype.onSnapshot;

		prototype.onSnapshot = function (...args) {
			const index = args.findIndex((arg) => isObserver(arg) || typeof arg === "function");
			const observer = isObserver(args[index])
				? args[index]
				: { next: args[index], error: args[index + 1], complete: args[index + 2] };

			const error = (error) => {
				if (error?.code === "permission-denied") {
					denied("listen", getPaths(this), error);
				}

				if (observer.error) {
					observer.error(error);
				}
			};

			return original.apply(this, [...args.slice(0, index), { ...observer, error }]);
		};
	};

	const documentPath = (ref) => [ref.path];
	const queryPaths = (query) => [queryPath(query)];

	wrapPromise(DocumentReference.prototype, "get", "get", documentPath);
	wrapPromise(DocumentReference.prototype, "set", "set", documentPath);
	wrapPromise(DocumentReference.prototype, "update", "update", documentPath);
	wrapPromise(DocumentReference.prototype, "delete", "delete", documentPath);
	wrapSnapshot(DocumentReference.prototype, documentPath);

	wrapPromise(Query.prototype, "get", "list", queryPaths);
	wrapSnapshot(Query.prototype, queryPaths);

	// Batches and transactions only say which document failed by refusing all of them
	const writtenPaths = new WeakMap();

	const record = (prototype, methods) => {
		for (const method of methods) {
			const original = prototype[method];

			prototype[method] = function (ref, ...args) {
				writtenPaths.set(this, [...(writtenPaths.get(this) || []), ref?.path]);

				return original.call(this, ref, ...args);
			};
		}
	};

	record(WriteBatch.prototype, ["set", "update", "delete"]);
	record(Transaction.prototype, ["get", "set", "update", "delete"]);

	wrapPromise(WriteBatch.prototype, "commit", "batch", (batch) => writtenPaths.get(batch) || []);

	const runTransaction = Firestore.prototype.runTransaction;

	Firestore.prototype.runTransaction = function (updateFunction) {
		let lastTransaction;

		return runTransaction
			.call(this, (transaction) => {
				lastTransaction = transaction;

				return updateFunction(transaction);
			})
			.catch((error) => rethrow("transaction", writtenPaths.get(lastTransaction) || [])(error));
	};
}
